// src/features/streams/subaccounts/SubAccountStreamsGrid.tsx

import type { MouseEvent } from 'react';
import { Box, Text } from '@mantine/core';
import { IconBroadcast, IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import type { SubAccountStreamRow, SubAccountStreamSortKey } from '@/lib/types/Stream';
import type { SortDirection } from '@/lib/types/common';
import styles from './SubAccountStreamsGrid.module.css';

interface SubAccountStreamsGridProps {
  rows: SubAccountStreamRow[];
  sortKey: SubAccountStreamSortKey;
  sortDirection: SortDirection;
  onSort: (key: SubAccountStreamSortKey) => void;
  /** Open a stream in its sub-account context (switches selected account). */
  onOpenStream: (row: SubAccountStreamRow) => void;
  hasAnyStreams: boolean;
}

interface HeaderColumn {
  key: SubAccountStreamSortKey;
  label: string;
  align?: 'left' | 'center' | 'right';
}

const HEADER_COLUMNS: HeaderColumn[] = [
  { key: 'account', label: 'Account', align: 'left' },
  { key: 'title', label: 'Title', align: 'left' },
  { key: 'liveStatus', label: 'Live Status', align: 'center' },
  { key: 'location', label: 'Location', align: 'left' },
  { key: 'status', label: 'Status', align: 'center' },
  { key: 'published', label: 'Published', align: 'right' },
  { key: 'updated', label: 'Updated', align: 'right' },
];

const DOT_CLASS_BY_STATUS = {
  live: 'statusDotLive',
  recording: 'statusDotRecording',
  offline: 'statusDotOffline',
} as const;

const LIVE_LABELS = {
  live: 'Live',
  recording: 'Live (Rec)',
  offline: 'Offline',
} as const;

export function SubAccountStreamsGrid({
  rows,
  sortKey,
  sortDirection,
  onSort,
  onOpenStream,
  hasAnyStreams,
}: SubAccountStreamsGridProps) {
  if (rows.length === 0) {
    return (
      <Box className={styles.gridContainer}>
        <Box className={styles.emptyState}>
          <IconBroadcast size={48} stroke={1.5} opacity={0.5} />
          <Text size="lg" mt="md" fw={500}>
            {hasAnyStreams ? 'No matching streams' : 'No sub-account streams'}
          </Text>
          <Text size="sm" c="dimmed" mt="xs">
            {hasAnyStreams
              ? 'Try adjusting your search or filters'
              : 'Streams created in your sub-accounts will appear here'}
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box className={styles.gridContainer}>
      <div className={`${styles.header} ${styles.gridColumns}`}>
        {HEADER_COLUMNS.map((col) => {
          const isActive = col.key === sortKey;
          return (
            <div
              key={col.key}
              className={`${styles.headerCell} ${styles.sortableHeader}`}
              style={{
                justifyContent: col.align === 'right' ? 'flex-end' :
                                col.align === 'center' ? 'center' : 'flex-start',
              }}
              onClick={() => onSort(col.key)}
            >
              {col.label}
              <span className={`${styles.sortIcon} ${isActive ? styles.sortIconActive : ''}`}>
                {isActive && sortDirection === 'asc' ? (
                  <IconChevronUp size={14} />
                ) : isActive && sortDirection === 'desc' ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronDown size={14} style={{ opacity: 0.3 }} />
                )}
              </span>
            </div>
          );
        })}
      </div>

      <div className={styles.body}>
        {rows.map((row) => (
          <SubAccountStreamRowEl key={row.mediaId} row={row} onOpenStream={onOpenStream} />
        ))}
      </div>
    </Box>
  );
}

function SubAccountStreamRowEl({
  row,
  onOpenStream,
}: {
  row: SubAccountStreamRow;
  onOpenStream: (row: SubAccountStreamRow) => void;
}) {
  const handleTitleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    // Plain left-click opens in-app (needs the account switch first);
    // modified clicks keep native <a> behavior.
    if (e.button === 0 && !e.ctrlKey && !e.metaKey && !e.shiftKey) {
      e.preventDefault();
      onOpenStream(row);
    }
  };

  return (
    <div className={`${styles.row} ${styles.gridColumns}`}>
      {/* Account: label + display cId */}
      <div className={`${styles.cell} ${styles.stackedCell}`}>
        <div className={styles.stackedContainer}>
          <span className={styles.primaryText}>{row.accountLabel}</span>
          <div className={styles.subline}>
            <span className={styles.sublineId}>{row.accountDisplayId}</span>
          </div>
        </div>
      </div>

      {/* Title: link into the stream (sub-account context) + display id + type */}
      <div className={`${styles.cell} ${styles.stackedCell}`}>
        <div className={styles.stackedContainer}>
          <a href={row.href} className={styles.titleLink} title={row.title} onClick={handleTitleClick}>
            {row.title}
          </a>
          <div className={styles.subline}>
            <span className={styles.sublineId}>{row.displayId}</span>
            <span className={styles.streamTypeBadge}>{row.streamTypeLabel}</span>
          </div>
        </div>
      </div>

      {/* Live status */}
      <div className={`${styles.cell} ${styles.cellLiveStatus}`}>
        <span className={styles.liveIndicator}>
          <span className={`${styles.statusDot} ${styles[DOT_CLASS_BY_STATUS[row.liveStatus]]}`} />
          <span
            className={`${styles.liveStatusLabel} ${
              row.liveStatus === 'offline' ? styles.liveStatusLabelOffline : styles.liveStatusLabelActive
            }`}
          >
            {LIVE_LABELS[row.liveStatus]}
          </span>
        </span>
      </div>

      {/* Location */}
      <div className={`${styles.cell} ${styles.cellLocation}`}>{row.locationLabel}</div>

      {/* Status badge */}
      <div className={`${styles.cell} ${styles.cellStatus}`}>
        <span className={styles.statusBadge} data-color={row.statusColor}>
          {row.statusLabel}
        </span>
      </div>

      {/* Published */}
      <div className={`${styles.cell} ${styles.cellDate}`}>
        {row.publishedFormatted ? (
          <span title={row.publishedTooltip ?? undefined}>{row.publishedFormatted}</span>
        ) : '-'}
      </div>

      {/* Updated */}
      <div className={`${styles.cell} ${styles.cellDate}`}>
        <span title={row.updatedTooltip}>{row.updatedFormatted}</span>
      </div>
    </div>
  );
}
